import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { classNames } from 'primereact/utils';
import axios from 'axios';
import baseURL, { endPoints } from './Config';

const AppTopbar = (props) => {
    const navigate = useNavigate();
    const notificationMenuRef = useRef(null);
    const [notificationMenuActive, setNotificationMenuActive] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loadingNotifications, setLoadingNotifications] = useState(false);
    const loginData = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('adminUser') || localStorage.getItem('userDetails') || '{}');
        } catch (error) {
            return {};
        }
    }, []);
    const accessToken = loginData?.data?.token || loginData?.data?.accessToken;
    const authHeaders = useMemo(
        () => ({
            authorization: `Bearer ${accessToken}`
        }),
        [accessToken]
    );

    const loadNotifications = async ({ silent = false, reactivateActionable = false } = {}) => {
        if (!accessToken) {
            setNotifications([]);
            setUnreadCount(0);
            return;
        }

        try {
            if (!silent) setLoadingNotifications(true);
            const params = new URLSearchParams();
            params.append('page', '1');
            params.append('limit', '8');
            params.append('sortBy', 'updatedAt');
            params.append('sortOrder', 'desc');
            if (reactivateActionable) params.append('reactivateActionable', 'true');

            const response = await axios.get(`${baseURL + endPoints.getMyNotifications(params.toString())}`, {
                headers: authHeaders
            });
            const payload = response?.data?.data || {};
            setNotifications(payload.data || []);
            setUnreadCount(payload.unreadCount || 0);
        } catch (error) {
            console.warn('Failed to load notifications', error?.response?.data?.message || error?.message);
        } finally {
            if (!silent) setLoadingNotifications(false);
        }
    };

    const markNotificationAsRead = async (notificationId) => {
        if (!notificationId || !accessToken) return;
        try {
            await axios.patch(
                `${baseURL + endPoints.markNotificationRead(notificationId)}`,
                {},
                {
                    headers: authHeaders
                }
            );
            setNotifications((prev) => prev.map((item) => (item._id === notificationId ? { ...item, isRead: true } : item)));
            setUnreadCount((prev) => Math.max(0, prev - 1));
        } catch (error) {
            console.warn('Failed to mark notification as read', error?.response?.data?.message || error?.message);
        }
    };

    const markAllNotificationsAsRead = async () => {
        if (!accessToken) return;
        try {
            await axios.patch(
                `${baseURL + endPoints.markAllNotificationsRead}`,
                {},
                {
                    headers: authHeaders
                }
            );
            setNotifications((prev) => prev.map((item) => ({ ...item, isRead: true })));
            setUnreadCount(0);
        } catch (error) {
            console.warn('Failed to mark all notifications as read', error?.response?.data?.message || error?.message);
        }
    };

    const handleNotificationClick = async (notification) => {
        if (!notification?.isRead) {
            await markNotificationAsRead(notification?._id);
        }

        setNotificationMenuActive(false);
        const repairOrderId = notification?.metadata?.repairOrderId;
        const movementId = notification?.metadata?.movementId;
        const destination = notification?.metadata?.destination;
        if (destination) {
            navigate(destination);
        } else if (repairOrderId) {
            navigate(`/repair-orders/${repairOrderId}`);
        } else if (movementId) {
            navigate('/machine-movements');
        } else {
            navigate('/repair-orders');
        }
    };

    const onNotificationButtonClick = (event) => {
        event?.preventDefault?.();
        event?.stopPropagation?.();
        setNotificationMenuActive((prevState) => !prevState);
    };

    useEffect(() => {
        if (!accessToken) return;
        loadNotifications({ reactivateActionable: true });

        const intervalId = setInterval(() => {
            loadNotifications({ silent: true });
        }, 30000);

        return () => clearInterval(intervalId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accessToken]);

    useEffect(() => {
        const onDocumentMouseDown = (event) => {
            if (!notificationMenuRef.current) return;
            if (!notificationMenuRef.current.contains(event.target)) {
                setNotificationMenuActive(false);
            }
        };

        document.addEventListener('mousedown', onDocumentMouseDown);
        return () => document.removeEventListener('mousedown', onDocumentMouseDown);
    }, []);

    return (
        <>
            <div className="layout-topbar">
                <div className="layout-topbar-left">
                    <button
                        type="button"
                        className="topbar-menu-button p-link"
                        aria-label={props.translate('Open navigation')}
                        aria-controls="syncro-navigation"
                        aria-expanded={props.mobileMenuActive}
                        onClick={props.onMenuButtonClick}
                    >
                        <i className="pi pi-bars" aria-hidden="true"></i>
                    </button>
                    <button type="button" className="syncro-mobile-brand p-link" aria-label={props.translate('Dashboard')} onClick={() => navigate('/')}>
                        <img src="assets/layout/images/SYNCRO.png" alt="Syncro" />
                    </button>
                </div>

                <div className="layout-topbar-right">
                    <ul className="layout-topbar-right-items">
                        {accessToken && (
                            <li ref={notificationMenuRef} className={classNames('notifications-item', { 'active-topmenuitem': notificationMenuActive })}>
                                <button type="button" className="p-link" aria-label="Notificaciones" aria-expanded={notificationMenuActive} onClick={onNotificationButtonClick}>
                                    <i className="topbar-icon pi pi-fw pi-bell" aria-hidden="true"></i>
                                    {unreadCount > 0 && <span className="topbar-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>}
                                    <span className="topbar-item-name">Notificaciones</span>
                                </button>

                                {notificationMenuActive && (
                                    <ul className="fadeInDown" style={{ minWidth: '24rem', maxHeight: '24rem', overflowY: 'auto' }}>
                                        <li role="menuitem">
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    padding: '0.75rem 1rem'
                                                }}
                                            >
                                                <span style={{ fontWeight: 600 }}>Notificaciones</span>
                                                <button
                                                    className="p-link"
                                                    onClick={markAllNotificationsAsRead}
                                                    disabled={!unreadCount}
                                                    style={{ fontSize: '0.85rem', color: unreadCount ? '#2563eb' : '#94a3b8' }}
                                                >
                                                    Marcar todas como leídas
                                                </button>
                                            </div>
                                        </li>

                                        {loadingNotifications && (
                                            <li role="menuitem">
                                                <div style={{ padding: '0.75rem 1rem', color: '#64748b' }}>Cargando notificaciones...</div>
                                            </li>
                                        )}

                                        {!loadingNotifications && notifications.length === 0 && (
                                            <li role="menuitem">
                                                <div style={{ padding: '0.75rem 1rem', color: '#64748b' }}>No tienes notificaciones.</div>
                                            </li>
                                        )}

                                        {!loadingNotifications &&
                                            notifications.map((notification) => (
                                                <li role="menuitem" key={notification._id}>
                                                    <button
                                                        className="p-link"
                                                        onClick={() => handleNotificationClick(notification)}
                                                        style={{
                                                            width: '100%',
                                                            textAlign: 'left',
                                                            padding: '0.75rem 1rem',
                                                            background: notification.isRead ? 'transparent' : 'rgba(37, 99, 235, 0.08)',
                                                            borderRadius: 0
                                                        }}
                                                    >
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                            {!notification.isRead && <i className="pi pi-circle-fill" style={{ fontSize: '0.5rem', color: '#2563eb' }}></i>}
                                                            <span style={{ fontWeight: notification.isRead ? 500 : 700 }}>
                                                                {notification.title || 'Nueva notificación'}
                                                            </span>
                                                        </div>
                                                        {notification.type === 'machine_inspection_update' && (
                                                            <span style={{ display: 'inline-block', marginTop: '0.4rem', padding: '0.2rem 0.45rem', borderRadius: '5px', fontSize: '0.7rem', fontWeight: 700, background: { bad: '#fff1f2', regular: '#fffbeb', good: '#ecfdf5' }[notification.metadata?.severity] || '#f1f5f9', color: { bad: '#be123c', regular: '#92400e', good: '#047857' }[notification.metadata?.severity] || '#475569' }}>
                                                                {notification.metadata?.status === 'closed' ? 'Finalizada' : { bad: 'Prioritaria', regular: 'Atención', good: 'Informativa' }[notification.metadata?.severity] || 'Inspección'}
                                                            </span>
                                                        )}
                                                        <div style={{ marginTop: '0.35rem', color: '#334155', fontSize: '0.85rem' }}>{notification.message}</div>
                                                        <div style={{ marginTop: '0.35rem', color: '#64748b', fontSize: '0.75rem' }}>
                                                            {notification.createdAt ? new Date(notification.createdAt).toLocaleString() : ''}
                                                        </div>
                                                    </button>
                                                </li>
                                            ))}
                                    </ul>
                                )}
                            </li>
                        )}
                    </ul>
                </div>
            </div>
        </>
    );
};

export default AppTopbar;
